/**
 * Copyright Mántum All Rights Reserved.
 */
import debug from 'debug';
import { ACTION_VALUE, useGroup } from '../../group';
import { forwardRef, useRef, useEffect, useState } from 'react';
import { validator } from '@mantum/kanela';

import type { Validation } from '@mantum/kanela';

const logger = debug('kanela');
const render = debug('kanela:render');

export type PropTypes = {
    disabled?: boolean;
    error?: boolean;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    placeholder?: string;
    testid?: string;
    validation?: Validation;
    value?: string;
};

export default forwardRef<HTMLTextAreaElement, PropTypes>(
    function Component(props, ref) {
        // prettier-ignore
        const {
        name,
        value = ''
    } = props

        const groups = useGroup();
        const changeRef = useRef(props.onChange);

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        const [currentValue, setCurrentValue] = useState('');

        useEffect(() => {
            setCurrentValue(!validator.isNull(value) ? value : '');
        }, [value]);

        useEffect(() => {
            if (changeRef.current) {
                changeRef.current(name, value);
            }

            return () => {
                if (changeRef.current) {
                    changeRef.current(name, '');
                }
            };
        }, [name, value]);

        const handleChange = (
            event: React.ChangeEvent<HTMLTextAreaElement>
        ) => {
            const { name, value } = event.target;

            setCurrentValue(value);
            if (changeRef.current) {
                changeRef.current(name, value);
            }
        };

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            if (!(name in groups)) {
                return;
            }

            const content = groups[name];
            if (content.action === ACTION_VALUE) {
                const value = `${content.value}`;
                setCurrentValue(value);

                logger('receive dispatch change value "%s": %s', name, value);
                if (changeRef.current) {
                    changeRef.current(name, value);
                }
            }
        }, [name, groups]);

        const style = !props.error
            ? 'px-2 border-gray-300'
            : 'pl-2 pr-8 border-2 border-error';

        logger('My group %o', groups); // TODO: REMOVE LINE
        render('[Textarea]: %o', {
            value,
            current: currentValue
        });

        return (
            <textarea
                ref={ref}
                className={`h-20 min-h-[80px] w-full resize-y rounded border p-2 text-base outline-primary disabled:bg-transparent disabled:opacity-90 ${style}`}
                data-testid={props.testid}
                disabled={props.disabled}
                name={props.name}
                onBlur={props.onBlur}
                onChange={handleChange}
                placeholder={props.placeholder}
                value={currentValue}
            />
        );
    }
);
